import { type JSX, useState } from 'react';
import Box from 'carbon-react/esm/components/box';
import Button from 'carbon-react/esm/components/button/__next__';
import styled from 'styled-components';
import Sort from 'carbon-react/esm/components/flat-table/sort';
import {
  FlatTable,
  FlatTableHead,
  FlatTableBody,
  FlatTableRow,
  FlatTableHeader,
  FlatTableCell,
  FlatTableCheckbox,
} from 'carbon-react/esm/components/flat-table';
import { Collaborator } from '../../../../types/collaborator.types';
import { blurOnClick } from '../../../../utils/blurOnClick';
import { CollaboratorsViewProps } from './CollaboratorsList';
import TablePager from '../../../../pages/dsn-list/component/TablePager';

// Carbon displays its focus ring for every focused button. Keep it for keyboard
// navigation, but avoid adding a second visible outline after a mouse click.
const CollaboratorActionButton = styled(Button)`
  &&&:focus:not(:focus-visible) {
    box-shadow: none !important;
    outline: none !important;
    border-color: transparent !important;
  }
`;

const CollaboratorsTable = ({
  collaborators,
  getProfileLabel,
  setSortValue: sortData,
  setSelectedCollaborator,
  setEditDialogId,
  setDeleteDialogId,
}: CollaboratorsViewProps): JSX.Element => {
  const pageSizeOptions = [5, 10, 20, 30, 50];
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [isAscending, setIsAscending] = useState<boolean>(true);
  const [sortField, setSortField] = useState<string>('lastName');

  const getSortType = (fieldName: string): 'ascending' | 'descending' | undefined => {
    if (sortField === fieldName) {
      return isAscending ? 'ascending' : 'descending';
    }
    return undefined;
  };

  const handleSortClick = (fieldName: string) => {
    const ascending = sortData(fieldName);
    setSortField(fieldName);
    setIsAscending(ascending);
  };

  const handlePagination = (newPage: number, newPageSize?: number) => {
    setCurrentPage(newPage);
    setPageSize(newPageSize ?? pageSize);
  };

  const renderRows = () => {
    const start = (currentPage - 1) * pageSize;
    const dataRows = collaborators.slice(start, start + pageSize);

    return dataRows.map((collab: Collaborator) => (
      <FlatTableRow key={collab.instanceId}>
        <FlatTableCell>{collab.firstName}</FlatTableCell>
        <FlatTableCell>{collab.lastName}</FlatTableCell>
        <FlatTableCell>{collab.email}</FlatTableCell>
        <FlatTableCheckbox checked={collab.isActive} onChange={() => {}} disabled />
        <FlatTableCell>{getProfileLabel(collab)}</FlatTableCell>
        <FlatTableCell>
          <Box display={'flex'} gap={3}>
            <CollaboratorActionButton
              onClick={e => {
                e.stopPropagation();
                blurOnClick(e);
                setEditDialogId(collab.instanceId);
                setSelectedCollaborator(collab);
              }}
              variantType="subtle"
              size="small"
              iconType="edit"
              aria-label="Modifier le collaborateur"
            />
            <CollaboratorActionButton
              onClick={e => {
                e.stopPropagation();
                blurOnClick(e);
                setDeleteDialogId(collab.instanceId);
                setSelectedCollaborator(collab);
              }}
              variantType="subtle"
              size="small"
              iconType="delete"
              aria-label="Supprimer le collaborateur"
            />
          </Box>
        </FlatTableCell>
      </FlatTableRow>
    ));
  };

  const desktopCollaboratorTable = (
    <>
      <div className="edi-list-table">
        <FlatTable
          width="100%"
          overflowX="auto"
          colorTheme="light"
          isZebra={true}
          hasMaxHeight={true}
          marginBottom={1}
        >
          <FlatTableHead>
            <FlatTableRow>
              <FlatTableHeader>
                <Sort
                  onClick={() => handleSortClick('firstName')}
                  {...{ sortType: getSortType('firstName') }}
                >
                  Prénom
                </Sort>
              </FlatTableHeader>
              <FlatTableHeader>
                <Sort
                  onClick={() => handleSortClick('lastName')}
                  {...{ sortType: getSortType('lastName') }}
                >
                  Nom
                </Sort>
              </FlatTableHeader>
              <FlatTableHeader>
                <Sort
                  onClick={() => handleSortClick('email')}
                  {...{ sortType: getSortType('email') }}
                >
                  Adresse mail
                </Sort>
              </FlatTableHeader>
              <FlatTableHeader>Actif</FlatTableHeader>
              <FlatTableHeader>
                <Sort
                  onClick={() => handleSortClick('profileId')}
                  {...{ sortType: getSortType('profileId') }}
                >
                  Profil
                </Sort>
              </FlatTableHeader>
              <FlatTableHeader></FlatTableHeader>
            </FlatTableRow>
          </FlatTableHead>
          <FlatTableBody>{renderRows()}</FlatTableBody>
        </FlatTable>
      </div>

      <TablePager
        totalRecords={collaborators.length}
        pageSize={pageSize}
        currentPage={currentPage}
        onPagination={(next, size) => handlePagination(next, size)}
        pageSizeSelectionOptions={pageSizeOptions.map(size => ({
          id: size.toString(),
          name: size,
        }))}
      />
    </>
  );

  return desktopCollaboratorTable;
};

export default CollaboratorsTable;
